import { useMemo } from 'react';

/**
 * Fuzzy matching to find service in appointment summary
 */
const findService = (summary, services) => {
  if (!summary || !services.length) return null;
  const normSummary = summary.toLowerCase();
  
  // 1. Try exact/includes
  let found = services.find(s => normSummary.includes(s.nombre.toLowerCase()));
  if (found) return found;
  
  // 2. Try significant words (>3 chars)
  for (const s of services) {
    const words = s.nombre.toLowerCase().split(/[ /+&,.-]+/).filter(w => w.length > 3);
    if (words.length > 0 && words.some(w => normSummary.includes(w))) return s;
  }
  
  // 3. Contextual fallback
  const isGeneric = normSummary.includes('✂️') || 
                    normSummary.includes('peluquería') || 
                    normSummary.includes('corte') || 
                    normSummary.includes('cita');
                    
  if (isGeneric) {
    return services.find(s => s.nombre.toLowerCase().includes('adulto')) || services[0];
  }
  
  return null;
};

/**
 * Hook to calculate monthly revenue and stats
 */
export const useMonthlyRevenue = (appointments = [], services = []) => {
  return useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Filter appointments for the current month
    const monthlyAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.fecha);
      const isCurrentMonth = aptDate.getMonth() === currentMonth && 
                            aptDate.getFullYear() === currentYear;
      
      const isNotBlocked = !apt.summary.toLowerCase().includes('bloqueado') && 
                          !apt.summary.toLowerCase().includes('cierre');
                          
      return isCurrentMonth && isNotBlocked;
    });

    // Filter ALL future appointments (from now onwards)
    const futureAppointments = appointments.filter(apt => {
      const aptDate = new Date(apt.fecha);
      const isNotBlocked = !apt.summary.toLowerCase().includes('bloqueado') && 
                          !apt.summary.toLowerCase().includes('cierre');
      return aptDate > now && isNotBlocked;
    });

    // Calculate total monthly revenue
    const total = monthlyAppointments.reduce((sum, apt) => {
      const service = findService(apt.summary, services);
      const price = service?.precio || 0;
      return sum + (typeof price === 'string' ? parseFloat(price) : price);
    }, 0);

    // Calculate future revenue projection
    const futureRevenue = futureAppointments.reduce((sum, apt) => {
      const service = findService(apt.summary, services);
      const price = service?.precio || 0;
      return sum + (typeof price === 'string' ? parseFloat(price) : price);
    }, 0);

    return {
      total,
      count: monthlyAppointments.length,
      futureCount: futureAppointments.length,
      futureRevenue,
      futureAppointments, // <--- Added this
      formatted: new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(total),
      formattedFuture: new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(futureRevenue)
    };
  }, [appointments, services]);
};

export default useMonthlyRevenue;
